import { FC, useState, useMemo } from "react";
import { Page } from "../../../Components/Page";
import { Link } from "react-router-dom";
import {
  ActionIcon,
  Avatar,
  Button,
  Group,
  Loader,
  Select,
  Skeleton,
  Table,
  TextInput,
} from "@mantine/core";
import { components } from "../../../openapi/generated-types";
import { DateTime } from "luxon";
import { IconEdit, IconHandStop, IconSearch } from "@tabler/icons-react";
import { useTeam } from "../../../hooks/useTeam.ts";
import { usePlayerList } from "../../../hooks/usePlayerList.ts";
import { useTeamList } from "../../../hooks/useTeamList.ts";

interface TableRowProps {
  player: components["schemas"]["Player"];
}

const TableRow: FC<TableRowProps> = (props) => {
  const { player } = props;

  const teamData = useTeam(player.teamId);

  const birthday = DateTime.fromISO(player.birthday);
  const age = Math.floor(DateTime.now().diff(birthday).as("year"));

  if (!teamData.isSuccess) {
    return <Skeleton />;
  }

  return (
    <Table.Tr>
      <Table.Td>
        <Avatar>
          {player.firstName.substring(0, 1).toUpperCase()}
          {player.lastName.substring(0, 1).toUpperCase()}
        </Avatar>
      </Table.Td>
      <Table.Td>{player.lastName}</Table.Td>
      <Table.Td>{player.firstName}</Table.Td>
      <Table.Td>
        {birthday.toFormat("dd.MM.yyyy")} ({age})
      </Table.Td>
      <Table.Td>
        <Group>
          {teamData.data.name} (Nr. {player.number})
          <ActionIcon
            component={Link}
            to={`${player.id}/change-team`}
            variant="subtle"
          >
            <IconEdit size={18} />
          </ActionIcon>
        </Group>
      </Table.Td>
      <Table.Td>{player.isGoalkeeper && <IconHandStop />}</Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon
            component={Link}
            to={`${player.id}/edit`}
            variant="subtle"
          >
            <IconEdit size={18} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

export const PlayerOverview: FC = () => {
  const playerData = usePlayerList();
  const teamData = useTeamList();
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState<string | null>(null);

  const filteredPlayers = useMemo(() => {
    if (!playerData.isSuccess) return [];

    return playerData.data.filter((player) => {
      const searchLower = search.toLowerCase().trim();
      const matchesSearch =
        searchLower === "" ||
        player.firstName.toLowerCase().includes(searchLower) ||
        player.lastName.toLowerCase().includes(searchLower) ||
        `${player.firstName} ${player.lastName}`
          .toLowerCase()
          .includes(searchLower) ||
        `${player.lastName} ${player.firstName}`
          .toLowerCase()
          .includes(searchLower);

      const matchesTeam = !teamFilter || player.teamId === teamFilter;

      return matchesSearch && matchesTeam;
    });
  }, [playerData.data, playerData.isSuccess, search, teamFilter]);

  if (!playerData.isSuccess || !teamData.isSuccess) {
    return <Loader />;
  }

  const teamOptions = teamData.data.teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));

  return (
    <Page
      title="Spieler"
      action={
        <Button component={Link} to="new">
          Neuer Spieler
        </Button>
      }
    >
      <Group gap="xs" wrap="wrap" mb="md">
        <TextInput
          placeholder="Suche nach Name..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          style={{ flexGrow: 1, minWidth: 200 }}
        />
        <Select
          placeholder="Mannschaft"
          data={teamOptions}
          value={teamFilter}
          onChange={setTeamFilter}
          clearable
          searchable
          style={{ minWidth: 200 }}
        />
      </Group>
      <Table.ScrollContainer minWidth={600}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Bild</Table.Th>
              <Table.Th>Nachname</Table.Th>
              <Table.Th>Vorname</Table.Th>
              <Table.Th>Geburtstag (Alter)</Table.Th>
              <Table.Th>Mannschaft</Table.Th>
              <Table.Th>Torwart</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredPlayers.map((p) => (
              <TableRow key={p.id} player={p} />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Page>
  );
};
